import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { api } from "@/lib/apiClient";
import { getUserDisplayName, useCurrentUser } from "@/lib/auth";
import { isBlankMailText, renderFinishedMail } from "@/lib/emailPreview";
import { useMailPlacePreview } from "@/lib/useMailPlacePreview";
import { canWriteStammdaten } from "@/lib/permissions";
import {
  applyPlaceholderPreview,
  BOOKING_PLACEHOLDERS,
  DUNNING_PLACEHOLDERS,
  EMAIL_LAYOUT_PLACEHOLDERS,
  INVOICE_PLACEHOLDERS,
  mailTokenLabel,
  SENDER_PLACEHOLDERS,
  senderPreviewValues,
  type PlaceholderDoc,
} from "@/lib/templatePlaceholders";
import { EmailHtmlEditor, type EmailHtmlEditorHandle } from "@/components/mail/EmailHtmlEditor";
import { PlaceholderGroups } from "@/pages/mail/bulkMailUi";
import { FinishedMailPreview } from "@/components/mail/FinishedMailPreview";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { StammdatenWriteHint } from "@/pages/stammdaten/StammdatenWriteHint";

const DEFAULT_LAYOUT_ID = "classic";

type MailId =
  | "booking"
  | "inquiry"
  | "rejection"
  | "invoice"
  | "dunning"
  | "phone_hold"
  | "comfort_check_in"
  | "guest_receipt"
  | "guest_receipt_inquiry"
  | "portal_login"
  | "supplier_order"
  | "tourist_confirmation";

type MailGroup = "stay" | "pay" | "more";

const MAIL_GROUPS: { id: MailGroup; label: string }[] = [
  { id: "stay", label: "Aufenthalt" },
  { id: "pay", label: "Zahlung" },
  { id: "more", label: "Weitere Mails" },
];

const LINK_PLACEHOLDERS: PlaceholderDoc[] = [
  { key: "completion_url", description: "Link, über den der Gast die Schnellbuchung abschließt" },
  { key: "hold_until", description: "Frist, bis zu der der Platz gehalten wird" },
  { key: "check_in_url", description: "Link zum Comfort Check-in" },
  { key: "login_code", description: "Anmeldecode für das Gästeportal" },
  { key: "code_valid_minutes", description: "Wie viele Minuten der Code gilt" },
  { key: "delivery_date", description: "Liefertag der Bestellung" },
  { key: "delivery_weekday", description: "Wochentag der Lieferung" },
  { key: "extra_cars", description: "Anzahl zusätzlicher Autos" },
  { key: "extra_motorbikes", description: "Anzahl zusätzlicher Motorräder" },
  { key: "companion_1", description: "Erste mitreisende Person" },
  { key: "companion_2", description: "Zweite mitreisende Person" },
  { key: "companion_3", description: "Dritte mitreisende Person" },
];

interface LayoutOption {
  id: string;
  name: string;
  description: string | null;
  source: string;
  html: string | null;
}

interface LayoutPreset {
  id: string;
  name: string;
  description: string;
  html: string;
}

interface CustomLayout {
  id: string;
  name: string;
  html: string;
}

interface InquiryVariant {
  subject: string | null;
  body: string | null;
  email_layout_id: string | null;
}

interface StoredSystemMail {
  subject: string | null;
  body: string | null;
  email_layout_id: string | null;
}

interface Templates {
  invoice_footer: {
    footer: string | null;
    email_intro: string | null;
    email_layout_id: string | null;
    email_subject: string | null;
  };
  booking_confirmation: { email_body: string | null; email_layout_id: string | null; email_subject: string | null };
  booking_inquiry_response: {
    subject: string | null;
    body: string | null;
    email_layout_id: string | null;
    acceptance: InquiryVariant;
    rejection: InquiryVariant;
  };
  dunning_reminder: { subject: string | null; body: string | null; email_layout_id: string | null };
  email_layouts: CustomLayout[];
  system_mails?: Record<string, StoredSystemMail>;
}

interface SettingsResponse {
  templates: Templates;
}

interface Suggestions {
  invoice_footer: { email_intro?: string | null; email_subject?: string | null };
  booking_confirmation: { email_body?: string | null; email_subject?: string | null };
  booking_inquiry_response: {
    subject?: string | null;
    body?: string | null;
    acceptance?: { subject?: string | null; body?: string | null };
    rejection?: { subject?: string | null; body?: string | null };
  };
  dunning_reminder: { subject?: string | null; body?: string | null };
  system_mails?: Record<string, { subject?: string | null; body?: string | null; email_layout_id?: string | null }>;
}

const MAIL_KINDS: {
  id: MailId;
  group: MailGroup;
  label: string;
  hint: string;
  system: boolean;
  inheritLayout?: boolean;
  placeholders: PlaceholderDoc[];
}[] = [
  {
    id: "booking",
    group: "stay",
    label: "Buchungsbestätigung",
    hint: "Die Zusage an den Gast, mit PDF",
    system: false,
    placeholders: [...BOOKING_PLACEHOLDERS, ...SENDER_PLACEHOLDERS],
  },
  {
    id: "inquiry",
    group: "stay",
    label: "Anfrage",
    hint: "Allgemeine Antwort auf eine Anfrage",
    system: false,
    placeholders: [...BOOKING_PLACEHOLDERS, ...SENDER_PLACEHOLDERS],
  },
  {
    id: "rejection",
    group: "stay",
    label: "Absage",
    hint: "Wenn die Anfrage abgelehnt wird",
    system: false,
    placeholders: [...BOOKING_PLACEHOLDERS, ...SENDER_PLACEHOLDERS],
  },
  {
    id: "phone_hold",
    group: "stay",
    label: "Schnellbuchung",
    hint: "Link, damit der Gast die Reservierung ergänzt",
    system: true,
    placeholders: [...BOOKING_PLACEHOLDERS, ...LINK_PLACEHOLDERS, ...SENDER_PLACEHOLDERS],
  },
  {
    id: "comfort_check_in",
    group: "stay",
    label: "Comfort Check-in",
    hint: "Kennzeichen und Online-Zahlung vor der Anreise",
    system: true,
    placeholders: [...BOOKING_PLACEHOLDERS, ...LINK_PLACEHOLDERS, ...SENDER_PLACEHOLDERS],
  },
  {
    id: "guest_receipt",
    group: "stay",
    label: "Eingangsbestätigung",
    hint: "Nach der Online-Buchung an den Gast",
    system: true,
    inheritLayout: true,
    placeholders: [...BOOKING_PLACEHOLDERS, ...SENDER_PLACEHOLDERS],
  },
  {
    id: "guest_receipt_inquiry",
    group: "stay",
    label: "Eingang einer Anfrage",
    hint: "Wenn online nur eine Anfrage ankommt",
    system: true,
    inheritLayout: true,
    placeholders: [...BOOKING_PLACEHOLDERS, ...SENDER_PLACEHOLDERS],
  },
  {
    id: "tourist_confirmation",
    group: "stay",
    label: "Reservierung an Tourist",
    hint: "Die gestaltete Mail an das Tourist-Postfach",
    system: true,
    placeholders: [...BOOKING_PLACEHOLDERS, ...LINK_PLACEHOLDERS, ...SENDER_PLACEHOLDERS],
  },
  {
    id: "invoice",
    group: "pay",
    label: "Rechnung",
    hint: "Einleitung der Rechnungs-Mail",
    system: false,
    placeholders: [...INVOICE_PLACEHOLDERS, ...SENDER_PLACEHOLDERS],
  },
  {
    id: "dunning",
    group: "pay",
    label: "Mahnung",
    hint: "Zahlungserinnerung mit Mahn-PDF",
    system: false,
    placeholders: [...DUNNING_PLACEHOLDERS, ...SENDER_PLACEHOLDERS],
  },
  {
    id: "portal_login",
    group: "more",
    label: "Gästeportal",
    hint: "Anmeldecode für das Gästeportal",
    system: true,
    placeholders: [...LINK_PLACEHOLDERS, ...SENDER_PLACEHOLDERS],
  },
  {
    id: "supplier_order",
    group: "more",
    label: "Lieferantenbestellung",
    hint: "Bestellung an den Lieferanten",
    system: true,
    placeholders: [...LINK_PLACEHOLDERS, ...SENDER_PLACEHOLDERS],
  },
];

function text(value: string | null | undefined): string {
  return value ?? "";
}

interface MailDraft {
  subject: string;
  body: string;
  layoutId: string;
}

function openLabel(subject: string, body: string): string | null {
  const subjectMissing = isBlankMailText(subject);
  const bodyMissing = isBlankMailText(body);
  if (subjectMissing && bodyMissing) return "Noch offen";
  if (subjectMissing) return "Betreff fehlt";
  if (bodyMissing) return "Text fehlt";
  return null;
}

function blankDrafts(): Record<MailId, MailDraft> {
  return Object.fromEntries(
    MAIL_KINDS.map((item) => [item.id, { subject: "", body: "", layoutId: DEFAULT_LAYOUT_ID }]),
  ) as Record<MailId, MailDraft>;
}

export function SystemMailStudio() {
  const { user } = useCurrentUser();
  const groups = useMemo(() => (user?.groups as string[] | undefined) ?? [], [user]);
  const place = useMailPlacePreview();
  const senderSamples = useMemo(
    () =>
      senderPreviewValues({
        name: getUserDisplayName(user),
        email: String(user?.email || user?.upn || ""),
      }),
    [user],
  );
  const previewSamples = useMemo(
    () => ({ ...place.samples, ...senderSamples }),
    [place.samples, senderSamples],
  );
  const canWrite = canWriteStammdaten(groups);
  const editorRef = useRef<EmailHtmlEditorHandle>(null);

  const [mailId, setMailId] = useState<MailId>("booking");
  const [drafts, setDrafts] = useState<Record<MailId, MailDraft>>(blankDrafts);
  const [saved, setSaved] = useState<Record<MailId, MailDraft>>(blankDrafts);
  const [suggestions, setSuggestions] = useState<Suggestions | null>(null);
  const [layouts, setLayouts] = useState<LayoutOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testNote, setTestNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [insertWhere, setInsertWhere] = useState<"subject" | "body">("body");

  const applyTemplates = useCallback((templates: Templates) => {
    const bookingLayout = templates.booking_confirmation.email_layout_id || DEFAULT_LAYOUT_ID;
    const inquiry = templates.booking_inquiry_response;
    const next = blankDrafts();
    next.booking = {
      subject: text(templates.booking_confirmation.email_subject),
      body: text(templates.booking_confirmation.email_body),
      layoutId: bookingLayout,
    };
    next.inquiry = {
      subject: text(inquiry?.subject),
      body: text(inquiry?.body),
      layoutId: inquiry?.email_layout_id || DEFAULT_LAYOUT_ID,
    };
    next.rejection = {
      subject: text(inquiry?.rejection?.subject),
      body: text(inquiry?.rejection?.body),
      layoutId: inquiry?.rejection?.email_layout_id || DEFAULT_LAYOUT_ID,
    };
    next.invoice = {
      subject: text(templates.invoice_footer.email_subject),
      body: text(templates.invoice_footer.email_intro),
      layoutId: templates.invoice_footer.email_layout_id || DEFAULT_LAYOUT_ID,
    };
    next.dunning = {
      subject: text(templates.dunning_reminder.subject),
      body: text(templates.dunning_reminder.body),
      layoutId: templates.dunning_reminder.email_layout_id || DEFAULT_LAYOUT_ID,
    };
    for (const item of MAIL_KINDS) {
      if (!item.system) continue;
      const stored = templates.system_mails?.[item.id];
      const inherited = item.inheritLayout ? bookingLayout : "";
      next[item.id] = {
        subject: text(stored?.subject),
        body: text(stored?.body),
        layoutId: stored?.email_layout_id || inherited || DEFAULT_LAYOUT_ID,
      };
    }
    setDrafts(next);
    setSaved(next);
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get<SettingsResponse>("/api/v1/settings"),
      api.get<LayoutOption[]>("/api/v1/settings/templates/email-layouts"),
      api.get<Suggestions>("/api/v1/settings/templates/suggestions"),
    ])
      .then(([settings, layoutOptions, suggestionPayload]) => {
        setSuggestions(suggestionPayload);
        applyTemplates(settings.templates);
        setLayouts(layoutOptions);
      })
      .catch((reason: Error) => setError(reason.message))
      .finally(() => setLoading(false));
  }, [applyTemplates]);

  useEffect(() => {
    load();
  }, [load]);

  const kind = MAIL_KINDS.find((item) => item.id === mailId) ?? MAIL_KINDS[0]!;
  const draft = drafts[mailId];
  const layoutHtml = layouts.find((item) => item.id === draft.layoutId)?.html
    ?? layouts.find((item) => item.id === DEFAULT_LAYOUT_ID)?.html
    ?? "";

  function patchDraft(patch: Partial<MailDraft>) {
    setDrafts((current) => ({ ...current, [mailId]: { ...current[mailId], ...patch } }));
  }

  function loadSuggestion() {
    const source = suggestions;
    if (!source) return;
    if (kind.system) {
      const suggestion = source.system_mails?.[mailId];
      patchDraft({
        subject: text(suggestion?.subject),
        body: text(suggestion?.body),
      });
      return;
    }
    if (mailId === "booking") {
      patchDraft({
        subject: text(source.booking_confirmation.email_subject) || draft.subject,
        body: text(source.booking_confirmation.email_body) || draft.body,
      });
    } else if (mailId === "inquiry") {
      patchDraft({
        subject: text(source.booking_inquiry_response.subject) || draft.subject,
        body: text(source.booking_inquiry_response.body) || draft.body,
      });
    } else if (mailId === "rejection") {
      patchDraft({
        subject: text(source.booking_inquiry_response.rejection?.subject) || draft.subject,
        body: text(source.booking_inquiry_response.rejection?.body) || draft.body,
      });
    } else if (mailId === "invoice") {
      patchDraft({
        subject: text(source.invoice_footer.email_subject) || draft.subject,
        body: text(source.invoice_footer.email_intro) || draft.body,
      });
    } else if (mailId === "dunning") {
      patchDraft({
        subject: text(source.dunning_reminder.subject) || draft.subject,
        body: text(source.dunning_reminder.body) || draft.body,
      });
    }
  }

  async function save() {
    if (!canWrite) return;
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await api.patch("/api/v1/settings/templates", payloadFor(mailId, draft));
      setSuccess(true);
      load();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  async function sendTest(event: FormEvent) {
    event.preventDefault();
    if (!canWrite) return;
    const to = testTo.trim();
    if (!to.includes("@")) {
      setError("Bitte eine E-Mail-Adresse eintragen.");
      return;
    }
    if (!layoutHtml.trim()) {
      setError("Das HTML-Design ist noch nicht geladen.");
      return;
    }
    const subject = applyPlaceholderPreview(draft.subject, previewSamples);
    setTesting(true);
    setError(null);
    setTestNote(null);
    try {
      await api.post("/api/v1/settings/templates/test-send", {
        to_address: to,
        subject: subject || kind.label,
        html: renderFinishedMail(layoutHtml, draft.body, "", {
          sampleOverrides: previewSamples,
          layoutId: draft.layoutId,
          ...(place.known ? { logoHtml: place.logoHtml, signatureHtml: place.signatureHtml } : {}),
        }),
      });
      setTestNote(`Test gesendet an ${to}.`);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Testversand fehlgeschlagen.");
    } finally {
      setTesting(false);
    }
  }

  function insertPlaceholder(key: string) {
    if (insertWhere === "subject") {
      patchDraft({ subject: `${draft.subject}{{${key}}}` });
      return;
    }
    editorRef.current?.insertPlaceholder(key);
  }

  return (
    <div className="space-y-4">
      {!canWrite ? <StammdatenWriteHint /> : null}
      <p className="text-sm text-label-secondary">
        Hier liegen alle Mails, die das System verschickt. Jede Vorlage hat denselben Editor:
        Betreff, Text und HTML-Design. Die Vorschau zeigt genau diesen Text. Ist der Text leer,
        bleibt die Vorschau leer. Orange markierte Mails haben noch keinen gespeicherten Betreff
        oder Text. Mit „Vorschlag laden“ übernehmen Sie einen fertigen Text, gespeichert wird er
        erst mit „Speichern“. Solange nichts gespeichert ist, geht weiterhin der bisherige
        Standardtext raus. PDF-Texte bleiben unter Stammdaten.
      </p>
      {error ? <Alert variant="error">{error}</Alert> : null}
      {MAIL_GROUPS.map((group) => (
        <div key={group.id} className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-label-tertiary">{group.label}</p>
          <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
            {MAIL_KINDS.filter((item) => item.group === group.id).map((item) => {
              const open = loading ? null : openLabel(saved[item.id]?.subject ?? "", saved[item.id]?.body ?? "");
              const selected = mailId === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  className={
                    selected
                      ? "rounded-xl border border-accent bg-accent-soft px-4 py-3 text-left ring-1 ring-accent"
                      : open
                        ? "rounded-xl border border-status-orange bg-status-orange/10 px-4 py-3 text-left"
                        : "rounded-xl border border-separator bg-card px-4 py-3 text-left hover:bg-fill-tertiary/50"
                  }
                  onClick={() => {
                    setMailId(item.id);
                    setSuccess(false);
                    setTestNote(null);
                    setInsertWhere("body");
                  }}
                >
                  <span className="flex items-center justify-between gap-2">
                    <span className="block text-sm font-semibold text-label">{item.label}</span>
                    {open ? (
                      <span className="shrink-0 rounded-full bg-status-orange/15 px-2 py-0.5 text-[0.65rem] font-semibold text-status-orange">
                        {open}
                      </span>
                    ) : null}
                  </span>
                  <span className="mt-0.5 block text-xs text-label-secondary">{item.hint}</span>
                </button>
              );
            })}
          </div>
        </div>
      ))}

      {loading ? (
        <p className="text-sm text-label-secondary">Laden…</p>
      ) : (
        <div className="grid items-start gap-4 xl:grid-cols-2">
          <Card title={kind.label} description={kind.hint}>
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="secondary" size="sm" disabled={!canWrite} onClick={loadSuggestion}>
                  Vorschlag laden
                </Button>
              </div>
              <PlaceholderGroups
                placeholders={kind.placeholders}
                onInsert={insertPlaceholder}
                targetLabel={insertWhere === "subject" ? "Betreff" : "Text"}
              />
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-label">Betreff</span>
                <input
                  className="eh-input w-full"
                  value={draft.subject}
                  disabled={!canWrite}
                  onFocus={() => setInsertWhere("subject")}
                  onChange={(event) => patchDraft({ subject: event.target.value })}
                />
              </label>
              <div>
                <span className="mb-1 block text-sm font-medium text-label">Text</span>
                <EmailHtmlEditor
                  key={mailId}
                  ref={editorRef}
                  value={draft.body}
                  layoutId={draft.layoutId}
                  disabled={!canWrite}
                  conditionFields={kind.placeholders.map((item) => ({
                    key: item.key,
                    label: mailTokenLabel(item.key),
                  }))}
                  onFocus={() => setInsertWhere("body")}
                  onChange={(value) => patchDraft({ body: value })}
                />
              </div>
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-label">HTML-Design</span>
                <select
                  className="eh-input w-full"
                  value={draft.layoutId}
                  disabled={!canWrite}
                  onChange={(event) => patchDraft({ layoutId: event.target.value })}
                >
                  {layouts.map((layout) => (
                    <option key={layout.id} value={layout.id}>
                      {layout.name}
                      {layout.source === "custom" ? " (eigen)" : ""}
                    </option>
                  ))}
                </select>
              </label>
              {canWrite ? (
                <div className="flex flex-wrap items-center gap-3">
                  <Button type="button" onClick={() => void save()} disabled={saving}>
                    {saving ? "Speichern…" : "Speichern"}
                  </Button>
                  {success ? <span className="text-sm text-status-green">Gespeichert.</span> : null}
                </div>
              ) : null}
            </div>
          </Card>
          <div className="xl:sticky xl:top-[calc(var(--eh-app-header-height)+0.75rem)] xl:z-20 xl:max-h-[calc(100dvh-var(--eh-app-header-height)-1.5rem)] xl:overflow-y-auto">
            <Card
              title="Vorschau"
              description="Genau der Text aus dem Editor. Ist der Text leer, bleibt die Vorschau leer. Steht Text drin, kommt er im gewählten Design. Name des Platzes, Logo und Grußformel kommen dann aus den Betriebsdaten. Anrede, Daten und Beträge des Gastes sind Beispiele."
            >
              <FinishedMailPreview
                layoutHtml={layoutHtml}
                layoutId={draft.layoutId}
                body={draft.body}
                subject={draft.subject}
                samples={previewSamples}
                className="h-[min(32rem,calc(100dvh-var(--eh-app-header-height)-12rem))]"
              />
              {canWrite ? (
                <form className="mt-3 flex flex-wrap items-end gap-2" onSubmit={(event) => void sendTest(event)}>
                  <label className="min-w-[14rem] flex-1 text-sm">
                    <span className="mb-1 block font-medium text-label">Test an</span>
                    <input
                      type="email"
                      required
                      className="eh-input w-full"
                      placeholder="name@example.com"
                      value={testTo}
                      onChange={(event) => setTestTo(event.target.value)}
                    />
                  </label>
                  <Button type="submit" variant="secondary" disabled={testing}>
                    {testing ? "Senden…" : "Test senden"}
                  </Button>
                  {testNote ? <span className="text-sm text-status-green">{testNote}</span> : null}
                </form>
              ) : null}
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}

function payloadFor(id: MailId, draft: MailDraft): Record<string, unknown> {
  const layout = draft.layoutId || DEFAULT_LAYOUT_ID;
  const body = draft.body.trim() || null;
  const subject = draft.subject.trim() || null;
  if (id === "booking") {
    return { booking_confirmation: { email_body: body, email_subject: subject, email_layout_id: layout } };
  }
  if (id === "invoice") {
    return { invoice_footer: { email_intro: body, email_subject: subject, email_layout_id: layout } };
  }
  if (id === "dunning") {
    return { dunning_reminder: { subject, body, email_layout_id: layout } };
  }
  if (id === "rejection") {
    return { booking_inquiry_response: { rejection: { subject, body, email_layout_id: layout } } };
  }
  if (id === "inquiry") {
    return { booking_inquiry_response: { subject, body, email_layout_id: layout } };
  }
  return { system_mails: { [id]: { subject, body, email_layout_id: layout } } };
}

export function EmailDesignStudio() {
  const { user } = useCurrentUser();
  const groups = useMemo(() => (user?.groups as string[] | undefined) ?? [], [user]);
  const canWrite = canWriteStammdaten(groups);
  const [presets, setPresets] = useState<LayoutPreset[]>([]);
  const [customLayouts, setCustomLayouts] = useState<CustomLayout[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get<SettingsResponse>("/api/v1/settings"),
      api.get<LayoutPreset[]>("/api/v1/settings/templates/email-layout-presets"),
    ])
      .then(([settings, layoutPresets]) => {
        setCustomLayouts(settings.templates.email_layouts ?? []);
        setPresets(layoutPresets);
      })
      .catch((reason: Error) => setError(reason.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function save() {
    if (!canWrite) return;
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await api.patch("/api/v1/settings/templates", {
        email_layouts: customLayouts
          .filter((layout) => layout.name.trim() && layout.html.trim())
          .map((layout) => ({ id: layout.id, name: layout.name.trim(), html: layout.html })),
      });
      setSuccess(true);
      load();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-4">
      {!canWrite ? <StammdatenWriteHint /> : null}
      <p className="text-sm text-label-secondary">
        Das Design ist der Rahmen jeder Mail: Logo, Farben und Grußformel. Unten stehen fertige
        Vorlagen. Mit „Übernehmen und anpassen“ wird eine davon zu Ihrem Design und steht danach
        in jeder Mail. Der Text landet im Platzhalter {"{{body}}"}. Schaltflächen setzen Sie im
        Text ein, nicht hier im Rahmen.
      </p>
      {error ? <Alert variant="error">{error}</Alert> : null}
      {loading ? <p className="text-sm text-label-secondary">Laden…</p> : null}
      {!loading ? (
        <>
          <div className="grid gap-3 lg:grid-cols-2">
            {presets.map((preset) => (
              <Card key={preset.id} title={preset.name} description={preset.description}>
                <div className="space-y-3">
                  <FinishedMailPreview
                    layoutHtml={preset.html}
                    body={"<p>vielen Dank für Ihre Nachricht.</p>"}
                    className="h-72"
                  />
                  {canWrite ? (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        setCustomLayouts((current) => [
                          ...current,
                          {
                            id: `custom-${crypto.randomUUID().slice(0, 12)}`,
                            name: `${preset.name} (angepasst)`,
                            html: preset.html,
                          },
                        ]);
                        setOpenIndex(customLayouts.length);
                        setSuccess(false);
                      }}
                    >
                      Übernehmen und anpassen
                    </Button>
                  ) : null}
                </div>
              </Card>
            ))}
          </div>
          <Card title="Eigene Designs" description="Diese Rahmen stehen danach in jeder Mail zur Auswahl.">
            <div className="space-y-4">
              {customLayouts.length === 0 ? (
                <p className="text-sm text-label-tertiary">Noch kein eigenes Design. Übernehmen Sie oben eine Vorlage.</p>
              ) : null}
              {customLayouts.map((layout, index) => (
                <div key={layout.id} className="space-y-3 rounded-xl border border-separator p-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      className="eh-input min-w-0 flex-1"
                      value={layout.name}
                      disabled={!canWrite}
                      onChange={(event) => {
                        const name = event.target.value;
                        setCustomLayouts((current) => current.map((item, i) => (i === index ? { ...item, name } : item)));
                      }}
                    />
                    {canWrite ? (
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          setCustomLayouts((current) => current.filter((_, i) => i !== index));
                          setOpenIndex(null);
                        }}
                      >
                        Entfernen
                      </Button>
                    ) : null}
                  </div>
                  <textarea
                    className="eh-input min-h-40 w-full font-mono text-xs"
                    value={layout.html}
                    disabled={!canWrite}
                    onFocus={() => setOpenIndex(index)}
                    onChange={(event) => {
                      const html = event.target.value;
                      setCustomLayouts((current) => current.map((item, i) => (i === index ? { ...item, html } : item)));
                    }}
                  />
                  <p className="text-xs text-label-secondary">
                    Platzhalter im Rahmen: {EMAIL_LAYOUT_PLACEHOLDERS.map((item) => `{{${item.key}}}`).join(", ")}
                  </p>
                  {openIndex === index ? (
                    <FinishedMailPreview layoutHtml={layout.html} body={"<p>vielen Dank für Ihre Nachricht.</p>"} />
                  ) : null}
                </div>
              ))}
              {canWrite ? (
                <div className="flex flex-wrap items-center gap-3">
                  <Button type="button" onClick={() => void save()} disabled={saving}>
                    {saving ? "Speichern…" : "Designs speichern"}
                  </Button>
                  {success ? <span className="text-sm text-status-green">Gespeichert.</span> : null}
                </div>
              ) : null}
            </div>
          </Card>
        </>
      ) : null}
    </div>
  );
}
